import React from "react";
import { Card, CardBody, CardHeader } from "@heroui/card";
import { Logo } from "../Logo/Logo";
import {
  UilExclamationTriangle,
  UilCheckCircle,
  UilQuestionCircle,
} from "@iconscout/react-unicons";

interface StatusMessageProps {
  orderNumber?: string;
  status: "not-found" | "delivered" | "completed" | "invalid-state";
}

export const StatusMessage: React.FC<StatusMessageProps> = ({
  orderNumber,
  status,
}) => {
  const getStatusContent = () => {
    switch (status) {
      case "not-found":
        return {
          title: "Order Not Found",
          message:
            "The order you are looking for could not be found or is not available for transfer.",
          icon: <UilQuestionCircle size="32" color="#9CA3AF" />,
          color: "text-gray-400",
        };
      case "delivered":
        return {
          title: "Delivery Complete",
          message:
            "This order has been successfully delivered. Thank you for your service!",
          icon: <UilCheckCircle size="32" color="#9CA3AF" />,
          color: "text-gray-400",
        };
      case "completed":
        return {
          title: "Transfer Complete",
          message: "This transfer has been completed successfully.",
          icon: <UilCheckCircle size="32" color="#34D399" />,
          color: "text-green-400",
        };
      case "invalid-state":
        return {
          title: "Invalid Order State",
          message:
            "This order is not in a state that allows for pickup or delivery.",
          icon: <UilExclamationTriangle size="32" color="#FBBF24" />,
          color: "text-yellow-400",
        };
      default:
        return {
          title: "Unknown Status",
          message: "Unable to determine the status of this order.",
          icon: <UilQuestionCircle size="32" color="#9CA3AF" />,
          color: "text-gray-400",
        };
    }
  };

  const content = getStatusContent();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md mx-auto">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>

        <Card className="bg-gray-700/60 backdrop-blur-sm border border-gray-600/30 shadow-2xl rounded-3xl">
          <CardHeader className="pb-0 pt-8 px-8 flex justify-center">
            <div className="text-center space-y-4 w-full">
              {orderNumber && (
                <h2 className="text-lg font-medium text-gray-300">
                  Order #{orderNumber}
                </h2>
              )}
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-gray-700/50 rounded-full flex items-center justify-center backdrop-blur-sm">
                  {content.icon}
                </div>
              </div>
              <h1
                className={`text-2xl font-bold tracking-tight ${content.color}`}
              >
                {content.title}
              </h1>
            </div>
          </CardHeader>

          <CardBody className="px-8 py-8 flex justify-center">
            <div className="text-center w-full">
              <p className="text-gray-400 text-sm leading-relaxed max-w-sm mx-auto">
                {content.message}
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};
